import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import type {
  BenchmarkInsightCompetitorsCompetitor,
  BenchmarkInsightCompetitorsCompset,
  BenchmarkInsightCompetitorsProposedCompset,
} from 'lumen/models';
import type { PrioritizedCategory } from 'lumen/models/benchmark-insight/competitors/proposed-compset';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  BENI_COMPETITORS_TABS,
  type CompetitorsBenchmarkTab,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

export default class BenchmarkInsightCompetitorsCompsetService extends Service {
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked
  selectedProposedCompsetPrioritizedCategory: PrioritizedCategory | null = null;

  fetchCompsetTask = task(
    { restartable: true },
    async (activeTab: CompetitorsBenchmarkTab) => {
      const typeKey =
        activeTab === BENI_COMPETITORS_TABS.COMPSET
          ? 'benchmarkinsight/compset'
          : 'benchmarkinsight/internal_compset';

      try {
        const compset =
          await this.store.queryRecord<BenchmarkInsightCompetitorsCompset>(
            'benchmark-insight/competitors/compset',
            {
              typeKey,
              subscription_id: this.hotelAndMarketService.hotel.id ?? null,
            },
          );

        return compset;
      } catch (error) {
        this.errorTracking.captureException(error);

        return null;
      }
    },
  );

  getProposedCompsetsTask = task(
    { restartable: true },
    async (successCallback: () => void) => {
      const proposedCompsets =
        await this.store.query<BenchmarkInsightCompetitorsProposedCompset>(
          'benchmark-insight/competitors/proposed-compset',
          {
            typeKey: 'benchmarkinsight/compset/proposals',
            subscription_id: this.hotelAndMarketService.hotel.id ?? null,
          },
        );

      successCallback();

      return proposedCompsets;
    },
  );

  saveCompsetTask = task(
    { restartable: true },
    async (successCallback: () => Promise<void>): Promise<void> => {
      if (!this.compset || !this.selectedProposedCompetitors.length) {
        return;
      }

      // While trying to empty the array, `this.compset.compset = []` gave a linting error, so I set the length to 0 instead.
      this.compset.compset.length = 0;

      this.selectedProposedCompetitors.forEach((competitor) => {
        this.compset?.compset.push(competitor);
      });

      try {
        await this.compset.save();

        this.toast.success(
          this.intl.t(
            'benchmark_insight.desired_compset.builder.toast.save.success',
          ),
          {
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          },
        );

        await successCallback();
      } catch (error) {
        this.errorTracking.captureException(error);
      }
    },
  );

  get compset(): BenchmarkInsightCompetitorsCompset | null {
    return this.fetchCompsetTask.last?.value ?? null;
  }

  get proposedCompsets(): BenchmarkInsightCompetitorsProposedCompset[] {
    if (!this.getProposedCompsetsTask.last?.value) {
      return [];
    }

    return [...this.getProposedCompsetsTask.last.value];
  }

  get selectedProposedCompetitors(): BenchmarkInsightCompetitorsCompetitor[] {
    return this.selectedProposedCompset?.compset ?? [];
  }

  get selectedProposedCompset(): BenchmarkInsightCompetitorsProposedCompset | null {
    if (!this.selectedProposedCompsetPrioritizedCategory) {
      return null;
    }

    return (
      this.proposedCompsets.find(
        (proposedCompset) =>
          proposedCompset.prioritizedCategory ===
          this.selectedProposedCompsetPrioritizedCategory,
      ) ?? null
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/competitors/compset': BenchmarkInsightCompetitorsCompsetService;
  }
}
